export const helpArticles = {
  'quick-start-guide': `

Policy Central helps law enforcement agencies identify conflicts and redundancies across their policy documents by automatically analyzing uploaded policies and creating reports showing where policies contradict each other or repeat information.

**Key features:**
- Upload and store policy documents
- Automatic analysis to find conflicts
- Reports organized by finding type
- Track changes over time

This guide walks you through uploading your first document and generating your first report.

## Step 1: Upload a Document

1. Click **Library** in the left sidebar
2. Click the **Upload Documents** button or drag a PDF file onto the page
3. Select your policy document (must be a PDF file, maximum 20MB)
4. Wait for the upload to complete

You'll see your document appear in the library with an "Uploading" status.

## Step 2: Wait for Processing

After uploading, your document goes through several stages:

- **Processing** - We're reading and organizing the content
- **Processed** - Ready to use in reports

This usually takes 2-5 minutes depending on document size. You can navigate away and come back later.

## Step 3: Generate a Report

1. Click **Policy Checker** in the left sidebar
2. Click the **Generate Report** button
3. Wait for the analysis to complete (timing varies based on document count and size)
4. Review your results

The report shows conflicts and redundancies between your policies, organized by finding type.

## What's Next?

- Upload more documents to build your policy library
- Generate reports regularly to catch new conflicts
- Bookmark important reports for quick access

> **Note:** You need at least one processed document in your library before you can generate a report.
`,

  'document-status-guide': `

Understanding what each status means helps you know when your documents are ready to use.

## Document Status

| Status | Description | Typical Duration |
|--------|-------------|------------------|
| **Uploading** | File transfer to secure storage | Under 1 minute |
| **Processing** | Text extraction and analysis preparation. Large PDFs are automatically split into smaller pieces. | 2-5 minutes (varies by document size and complexity) |
| **Processed** | Ready for analysis. You can generate reports, view the document, or delete it. | - |
| **Failed** | Processing error. Common causes: corrupted PDF, password-protected file, or file over 20MB. Try uploading again. | - |

## While Processing

You can navigate away from the Library page while documents process. The system will continue working in the background.

To check progress, return to the Library page and look at the status column.

---

**Related Articles:**
- [Upload Issues](/help/troubleshooting/upload-issues)
`,

  'cant-generate-report': `If the "Generate Report" button is disabled or you see an error message, here are the most common reasons and solutions.

## Common Issues

### No Processed Documents

**Problem:** You need at least one processed document in your library before you can generate a report.

**Solution:**
1. Go to the Library page
2. Check if you have any documents with "Processed" status
3. If all documents show "Processing", wait for them to finish
4. If you have no documents, upload at least one policy PDF

### Active Operations Running

**Problem:** The system is already processing something (uploading, syncing, or generating a report).

**Solution:** Wait for the current operation to complete. You'll see a notification in the bottom right corner showing what's running. Once it finishes, you can generate your report.

### All Documents Failed

**Problem:** If all your documents failed to process, there's nothing to analyze.

**Solution:**
1. Check the Library for documents with "Failed" status
2. Try re-uploading those documents
3. Make sure your PDFs are not password-protected or corrupted

## Still Can't Generate?

If you've checked all these issues and still can't generate a report:

1. Refresh the page and try again
2. Check that you're logged in
3. Contact support with details about what you're seeing

---

**Related Articles:**
- [Document Status Guide](/help/documents/document-status-guide)
- [Active Operations Explained](/help/troubleshooting/active-operations)
`,

  'understanding-conflicts': `

When you generate a report, Policy Central identifies conflicts and redundancies between your policy documents. Here's what these terms mean.

## What is a Conflict?

A conflict occurs when two or more policies contain statements that contradict each other or could lead to confusion when applied together.

**Example:**
- Policy A says: "Officers must submit reports within 24 hours"
- Policy B says: "Reports are due within 48 hours of the incident"

These create uncertainty about the actual deadline.

## What is a Redundancy?

A redundancy occurs when multiple policies say the same thing or cover the same topic without adding new information.

**Example:**
- Policy A: "All officers must wear badges"
- Policy B: "Badge display is required for all sworn personnel"

While not contradictory, having the same rule in multiple places can cause confusion if one gets updated and the other doesn't.

## Why Conflicts Matter

Conflicts create problems because:

- **Unclear expectations** - Staff don't know which rule to follow
- **Inconsistent application** - Different people may interpret things differently
- **Legal risk** - Contradictions can be used to challenge decisions
- **Training issues** - New staff get confused by mixed messages

## What to Do About Conflicts

When you find a conflict:

1. **Review both policies** - Understand what each one says
2. **Determine which is correct** - Decide which version should apply
3. **Update or remove** - Either update one policy to match the other, or remove the outdated one
4. **Document the decision** - Keep a record of why you chose one approach

## What is a Similarity?

A similarity occurs when two policies cover related topics where knowing about both improves operational awareness. They don't contradict each other, but an officer following one should know the other exists.

**Example:**
- Policy A covers pursuit initiation requirements
- Policy B covers supervisor approval criteria for pursuits

An officer initiating a pursuit needs to know both.

---

**Related Articles:**
- [Classification Types](/help/analysis/classification-types)
- [Reading Report Results](/help/reports/reading-report-results)
`,

  'classification-types': `

Policy Central classifies each finding as one of three types. Understanding the difference helps you prioritize which issues to review first.

## Classification Types

### Conflict

**What it means:** Two policies contain contradictory requirements for the exact same situation. An officer cannot comply with both at the same time.

**Example:**
- Policy A: "Officers must activate body cameras before initiating a traffic stop"
- Policy B: "Body cameras shall not be activated until the vehicle is stopped"

**What to do:** Conflicts need attention. Determine which policy reflects the correct procedure and update or remove the conflicting one.

### Redundancy

**What it means:** The same requirement or procedure appears in multiple policies without adding new information. If one is later updated but the other isn't, officers may follow outdated guidance.

**Example:**
- Policy A: "Evidence must be logged within 2 hours of collection"
- Policy B: "All collected evidence requires logging within 2 hours"

**What to do:** Consolidate redundant policies into a single authoritative source, or add cross-references to make clear which document is the controlling version.

### Similarity

**What it means:** Related content where an officer reading one policy should be aware the other exists. Not a contradiction — but knowing about both improves situational awareness.

**Example:**
- Policy A: "High-speed pursuits require supervisor approval"
- Policy B: "Supervisors must consider traffic density, weather, and offense severity before approving pursuits"

**What to do:** Add cross-references between the policies so officers can find related guidance easily.

## Prioritizing Your Work

Focus on **Conflicts** first — they create operational risk when officers must choose between contradictory rules. Review **Redundancies** next to reduce drift risk. **Similarities** can be addressed during regular policy reviews by adding cross-references.

---

**Related Articles:**
- [Understanding Conflicts](/help/reports/understanding-conflicts)
- [Reading Report Results](/help/reports/reading-report-results)
`,

  'active-operations': `

You'll sometimes see a notification in the bottom right corner showing "Active Operations." This tells you what Policy Central is currently working on.

## What Are Active Operations?

Active operations are tasks the system is processing in the background:

- **Uploading documents** - Transferring files to the server
- **Processing documents** - Reading and indexing content
- **Generating reports** - Analyzing policies for conflicts

## Why It Matters

You can only do one major operation at a time. If you're generating a report, you can't upload new documents until it finishes. This prevents the system from getting overloaded.

## What You Can Do

### During Active Operations

**You can:**
- Navigate around the app
- View your library
- Read existing reports
- Browse the help center

**You cannot:**
- Upload new documents
- Generate new reports
- Delete documents that are processing

### Checking Progress

The active operations indicator shows:
- What's currently running
- Progress (if available)
- Estimated time remaining (for some operations)

## If Something Gets Stuck

Most operations complete in a few minutes. If you see the same operation running for more than 10 minutes:

1. **Refresh the page** - Sometimes the indicator doesn't update properly
2. **Check your internet** - A lost connection can pause uploads
3. **Wait a bit longer** - Large documents take more time
4. **Contact support** - If it's been over 30 minutes, something might be wrong

## After Operations Complete

When an operation finishes, you'll see:
- A success message (or error if something went wrong)
- The active operations indicator disappears
- You can start the next operation

The system automatically clears completed operations from the indicator.

---

**Related Articles:**
- [Document Status Guide](/help/documents/document-status-guide)
- [Generating Reports](/help/reports/generating-reports)
`,

  'upload-issues': `

Having trouble uploading documents? Here are the most common issues and how to fix them.

## File Requirements

Before uploading, make sure your file meets these requirements:

- **Format:** PDF only
- **Size:** Maximum 20MB
- **Password:** Not password-protected or encrypted
- **Integrity:** File is not corrupted

## Common Problems

### File Type Not Supported

**Problem:** You're trying to upload a file that isn't a PDF.

**Solution:** Convert your document to PDF format first. Most word processors can save or export as PDF.

### File Too Large

**Problem:** Your PDF is larger than 20MB.

**Solution:**
- Reduce the file size using a PDF compression tool
- Split large documents into smaller sections
- Remove high-resolution images if possible

### Upload Fails or Gets Stuck

**Problem:** The upload starts but never completes, or fails with an error.

**Possible causes and solutions:**

**Internet connection issue**
- Check your internet connection
- Try uploading from a different network
- If on WiFi, move closer to the router

**Browser issue**
- Refresh the page and try again
- Clear your browser cache
- Try a different browser (Chrome, Firefox, Safari, or Edge)

**File is corrupted**
- Try opening the PDF on your computer first
- If it won't open, the file may be damaged
- Re-save or export the document

### Password-Protected PDFs

**Problem:** Your PDF has a password or security settings.

**Solution:** Remove the password before uploading:
1. Open the PDF in Adobe Acrobat or similar software
2. Remove security/password protection
3. Save the file
4. Try uploading again

### Duplicate File Warning

**Problem:** You see a message that this file has already been uploaded.

**Solution:** Check your Library page. If the document is already there, you don't need to upload it again. If you need to update it, delete the old version first.

### Processing Takes Too Long

**Problem:** Document uploads successfully but stays in "Processing" status for over 10 minutes.

**Normal processing time:**
- Small documents (1-10 pages): 1-2 minutes
- Medium documents (11-50 pages): 2-4 minutes
- Large documents (50+ pages): 4-6 minutes

**Solutions:**

**First, refresh the page** - The status display may not have updated automatically.

**Wait if it's a large file** - Very large or complex documents can take up to 15 minutes.

**If stuck over 30 minutes:**
1. Delete the stuck document
2. Wait a minute
3. Check the file isn't corrupted (try opening it on your computer)
4. Upload again

**If multiple documents get stuck** - Contact support with document names, file sizes, and processing times.

## Still Having Problems?

If none of these solutions work:

1. Note the exact error message you see
2. Check the file size and name
3. Contact support with these details

---

**Related Articles:**
- [Uploading Documents](/help/documents/uploading-documents)
- [Document Status Guide](/help/documents/document-status-guide)
`,

  'how-analysis-works': `

Understanding how Policy Central analyzes your documents helps you get the most value from the system.

## The Process

When you generate a report, here's what happens:

### 1. Document Collection

The system gathers all your processed documents from the library. Only documents with "Processed" status are included in the analysis.

### 2. Content Analysis

Policy Central reads through all your policies and identifies:
- Key requirements and rules
- Procedural steps and deadlines
- Responsibilities and authorities
- Definitions and terms

### 3. Comparison

The system compares statements across all your policies, looking for:
- **Contradictions** - Rules that conflict with each other
- **Redundancies** - The same rule stated in multiple places
- **Similarities** - Related policies where cross-awareness adds operational value

### 4. Classification

Each finding is assigned one of three types:
- **Conflict** — Contradictory requirements an officer cannot satisfy simultaneously
- **Redundancy** — Identical content duplicated across policies
- **Similarity** — Related content where cross-awareness adds operational value

### 5. Report Generation

All findings are organized into a report showing:
- Total number of conflicts found
- Breakdown by type (Conflict, Redundancy, Similarity)
- Specific details for each conflict
- The documents and sections involved

## What Makes a Good Analysis?

The analysis works best when:

- **You have multiple policies** - More documents mean more comparisons
- **Policies are clearly written** - Well-structured documents are easier to analyze
- **Documents are up to date** - Old or draft versions may cause false conflicts
- **Content is complete** - Partial or summary documents may miss important details

## What the Analysis Doesn't Do

Policy Central helps identify potential conflicts, but it:

- **Doesn't make decisions** - You still need to review and determine what's actually a problem
- **Doesn't edit policies** - It shows you issues but doesn't change your documents
- **Doesn't understand context** - Some apparent conflicts may be intentional differences
- **Doesn't replace human judgment** - Use it as a tool to support your review process

## Using the Results

The analysis is most valuable when you:

1. **Review findings with your team** - Discuss whether flagged items are real issues
2. **Prioritize by type** - Address Conflicts first, then Redundancies, then Similarities
3. **Update your policies** - Make changes based on what you discover
4. **Re-run analysis** - Generate new reports after making updates to verify improvements

## Analysis Limitations

Keep in mind:

- **Results are suggestions** - Not every flagged item is necessarily wrong
- **Context matters** - Some differences between policies are intentional
- **It's a tool** - Use it to support your review process, not replace it

---

**Related Articles:**
- [Understanding Conflicts](/help/reports/understanding-conflicts)
- [Classification Types](/help/analysis/classification-types)
- [Reading Report Results](/help/reports/reading-report-results)
`,

  'uploading-documents': `

Learn how to add policy documents to your Policy Central library.

## Before You Upload

Make sure your document:
- Is in PDF format
- Is 20MB or smaller
- Is not password-protected
- Contains readable text (not just scanned images)

## How to Upload

### Option 1: Drag and Drop

1. Go to the **Library** page
2. Drag your PDF file from your computer
3. Drop it anywhere on the page
4. Wait for the upload to complete

### Option 2: File Picker

1. Go to the **Library** page
2. Click the **Upload Documents** button
3. Select one or more PDF files
4. Click **Open**
5. Wait for the upload to complete

## Upload Multiple Documents

You can upload multiple files at once:
- Drag and drop several files together, or
- Select multiple files using the file picker

Each file processes independently. You can upload more files while others are still processing.

## What Happens to Uploaded Files?

- Files are stored securely in your organization's library
- Only users in your organization can access them
- Documents remain in your library until you delete them
- You can view, download, or delete documents anytime

## Tips for Best Results

- **Use clear filenames** - This helps you find documents later
- **Upload complete policies** - Partial documents may cause incomplete analysis
- **Keep policies current** - Remove outdated versions to avoid confusion
- **Upload related policies together** - This helps find conflicts between them

---

**Related Articles:**
- [Document Status Guide](/help/documents/document-status-guide)
- [Upload Issues](/help/troubleshooting/upload-issues)
`,

  'navigating-the-interface': `

Policy Central has a simple layout designed to help you move quickly between key features.

## Main Navigation

The left sidebar contains the main navigation menu:

- **Library** - View and manage all your uploaded policy documents. This is where you upload new files, check processing status, and delete documents you no longer need.
- **Policy Checker** - Generate analysis reports to find conflicts between your policies. This is the main workspace where you'll spend most of your time reviewing results.

## Header Area

The top right corner contains:

User menu items include:
- **Help Center** - Access this documentation
- **Report a Bug** - Send feedback or report issues
- **Sign Out** - Log out of your account

## Page Titles

Each page displays its title at the top, so you always know where you are:
- Library icon for document management
- Policy Checker icon for analysis reports
- Support icon for help and bug reports

## Tips for Navigation

- **Click the sidebar items** to move between pages
- **Your work saves automatically** - No need to manually save
- **Use browser back/forward** buttons if needed
- **Refresh the page** if something doesn't look right

## Keyboard Navigation

You can use your keyboard to navigate:
- **Tab** - Move between buttons and links
- **Enter** - Activate buttons or links
- **/** - Jump to search (in Help Center)
`,

  'managing-your-library': `

Your library is where all uploaded policy documents are stored. Here's how to keep it organized.

## Viewing Your Library

Go to the **Library** page from the left sidebar. You'll see a list of all your documents with:
- Document name
- Upload date
- Current status
- File size

## Sorting Documents

Click column headers to sort your documents by:
- **Name** - Alphabetical order
- **Date** - Most recent first or oldest first
- **Status** - Group by processing stage

## Finding Documents

Use the search box at the top of the library to find documents by name. Type any part of the filename and the list will filter automatically.

## Document Information

Click on any document to view details:
- Full filename
- Upload date and time
- File size
- Current processing status
- Actions available (view, delete)

## Organizing Tips

**Use clear filenames**
Name your files descriptively before uploading. For example:
- "Use of Force Policy 2024.pdf" is better than "policy1.pdf"

**Remove outdated versions**
When you upload an updated policy, delete the old version to avoid analyzing outdated content.

**Keep related policies together**
Upload all policies from the same topic area so they can be analyzed together.

**Regular maintenance**
Periodically review your library and remove documents you no longer need.

## Viewing Documents

You can view any processed document directly in Policy Central.

**To view a document:**
1. Find the document in your library
2. Click the document name or view icon
3. The document opens in a viewer

**Viewer features:**
- Scroll through pages using mouse wheel or touchpad
- Zoom in/out with controls or keyboard shortcuts (Ctrl/Cmd + and -)
- Search for specific text within the document
- Navigate to specific page numbers

**Note:** Only documents with "Processed" status can be viewed. Documents still uploading or processing must finish first.

## Storage Limits

Your library can hold hundreds of documents. Each document must be:
- PDF format
- Under 20MB in size
- Readable text (not just scanned images)

There's no limit to how many documents you can upload, but keeping your library focused on active policies makes reports more useful.

## Deleting Documents

To remove a document from your library:
1. Find the document in your list
2. Click the delete icon or action menu
3. Confirm the deletion

**Note:** Deleting a document is permanent and cannot be undone. The document will be removed from all future reports.

---

**Related Articles:**
- [Uploading Documents](/help/documents/uploading-documents)
- [Deleting Documents](/help/documents/deleting-documents)
`,

  'deleting-documents': `

Remove documents from your library when you no longer need them for analysis.

## When to Delete Documents

Consider deleting documents when:
- **You have an updated version** - Remove old versions after uploading new ones
- **The policy is no longer active** - Retired or superseded policies
- **You uploaded by mistake** - Wrong file or duplicate upload
- **The document failed processing** - Remove failed uploads before trying again

## How to Delete a Document

1. Go to the **Library** page
2. Find the document you want to remove
3. Click the delete icon (trash can) next to the document
4. Confirm you want to delete it
5. The document is removed immediately

## What Happens When You Delete

**The document is permanently removed:**
- Cannot be recovered after deletion
- Will not appear in future analysis reports
- Removed from your document library
- No longer counts toward your library

**Existing reports are not affected:**
If you already generated reports that included this document, those reports remain unchanged. Only new reports will exclude the deleted document.

## Cannot Delete During Processing

You cannot delete a document while it's:
- Currently uploading
- Being processed
- Part of an active report generation

Wait for the operation to complete, then you can delete the document.

## Alternative to Deleting

If you're not sure whether to delete a document, consider:
- **Leaving it in your library** - It won't hurt to keep extra documents
- **Renaming it** - Add "[OLD]" or "[ARCHIVED]" to the filename before uploading
- **Downloading a backup** - Save a copy to your computer before deleting

## Bulk Deletion

Currently, you must delete documents one at a time. If you need to remove many documents, delete them individually or contact support for assistance.

## Recovering Deleted Documents

Deletion is permanent. If you delete a document by mistake:
1. Upload it again if you have the original file
2. Contact support immediately if you need help recovering it (success not guaranteed)

The best practice is to keep backup copies of important policies on your computer or network drive.

---

**Related Articles:**
- [Managing Your Library](/help/documents/managing-your-library)
- [Uploading Documents](/help/documents/uploading-documents)
`,

  'generating-reports': `

Create analysis reports to find conflicts and redundancies between your policy documents.

## Before You Generate

Make sure you have:
- At least one document with "Processed" status in your library
- No active operations currently running
- All documents you want to analyze are fully processed

## How to Generate a Report

1. Go to the **Policy Checker** page
2. Click the **Generate Report** button
3. Wait for the analysis to complete (timing varies based on document count and size)
4. Review your results

The report appears automatically when it's ready.

## What Gets Analyzed

Each report includes:
- **All processed documents** in your library
- Documents must have "Processed" status
- Failed or still-processing documents are excluded

You cannot select specific documents for analysis. The system always analyzes your entire current library.

## How Long It Takes

Report generation time depends on the size and number of your documents:
- **Smaller files:** A few minutes
- **Larger files or more documents:** Several minutes to an hour

The time varies based on total content size. You'll see a progress indicator while the report generates.

You'll see a progress indicator while the report generates. You can't start other operations until it finishes, but you can navigate around the app.

## After Generation

Once complete, you'll see:
- Total findings
- Breakdown by type (Conflict, Redundancy, Similarity)
- Detailed findings
- References to specific documents and sections

## Generating Multiple Reports

You can generate as many reports as you want. Common reasons to generate new reports:

- **After uploading new documents** - See if they conflict with existing policies
- **After updating policies** - Verify your changes resolved conflicts
- **Regular reviews** - Monthly or quarterly policy audits
- **Before important changes** - Check the current state before making updates

## Report History

All generated reports are saved automatically. Access past reports from the History sidebar to compare changes over time.

## If Generation Fails

If report generation fails:
1. Check that you have processed documents
2. Make sure no other operations are running
3. Refresh the page and try again
4. Contact support if problems continue

---

**Related Articles:**
- [Can't Generate Report](/help/troubleshooting/cant-generate-report)
- [Reading Report Results](/help/reports/reading-report-results)
- [Report History](/help/reports/report-history)
`,

  'reading-report-results': `

Understanding your analysis report helps you identify and prioritize policy conflicts.

## Report Overview

At the top of each report, you'll see:

**Total Findings**
The overall number of potential conflicts and redundancies identified across all your policies.

**Breakdown by Type**
How many findings fall into each category:
- **Conflict** - Contradictory requirements an officer cannot satisfy simultaneously
- **Redundancy** - Identical content duplicated across policies
- **Similarity** - Related content where cross-awareness adds operational value

## Finding Details

Each finding in the report shows:

**Finding Description**
A plain-language summary of what's conflicting or redundant.

**Finding Type**
Conflict, Redundancy, or Similarity — indicating the nature of the issue.

**Affected Documents**
Which policy documents contain the conflicting statements.

**Specific Sections**
Page numbers or section references where the conflict appears.

**Content Excerpts**
Relevant text from each document showing the conflicting statements.

## How to Read a Conflict

Here's what to look for in each conflict:

1. **Read the description** - Understand what the system found
2. **Check the type** - Decide if this needs immediate attention
3. **Review both documents** - Look at the full context, not just the excerpts
4. **Determine if it's real** - Some flagged items may not be actual problems
5. **Decide on action** - Update one policy, consolidate both, or note it's intentional

## Types of Findings

**Conflicts**
Two policies say opposite things about the same situation. Address these first.

**Redundancies**
Multiple policies say the same thing. Consolidate to prevent drift.

**Similarities**
Related content where knowing about both policies improves operational awareness.

## Not Every Finding is a Problem

Remember that:
- **Some differences are intentional** - Different situations may require different approaches
- **Context matters** - The excerpts may not show the full picture
- **It's a guide, not a ruling** - Use your judgment about what needs fixing

## Using the Results

**For Conflicts:**
1. Review with your team immediately
2. Determine which policy should prevail
3. Update or remove the conflicting content
4. Generate a new report to verify the fix

**For Redundancies:**
1. Add to your review list
2. Consolidate into a single authoritative source
3. Add cross-references if full consolidation isn't practical

**For Similarities:**
1. Review during scheduled policy audits
2. Add cross-references so officers can find related guidance
3. No urgent action required

## Sorting and Filtering

You can organize the report view:
- **Sort by type** - See Conflicts first
- **Filter by document** - Show only conflicts involving specific policies
- **Search within results** - Find specific terms or topics

## Saving Your Analysis

The report is automatically saved in your Report History. You can:
- Return to it later for reference
- Compare with future reports
- Share findings with your team (download as PDF)

---

**Related Articles:**
- [Understanding Conflicts](/help/reports/understanding-conflicts)
- [Classification Types](/help/analysis/classification-types)
- [Generating Reports](/help/reports/generating-reports)
`,

  'report-history': `

All generated reports are automatically saved so you can review past analyses and track changes over time.

## Accessing Report History

The report history appears in a sidebar on the Policy Checker page. You'll see:
- Date and time each report was generated
- Number of conflicts found
- Quick link to open that report

## Opening Past Reports

Click any report in the history to view it. The report opens in the main area, showing the same details you saw when it was first generated.

## Why Report History Matters

**Track progress over time**
Compare older reports to newer ones to see if you're reducing conflicts in your policy system.

**Reference past findings**
Return to earlier reports to check what conflicts existed before you made changes.

**Verify improvements**
After updating policies, generate a new report and compare it to the previous version to confirm conflicts are resolved.

**Document your work**
Keep a record of when you reviewed policies and what issues were found.

## Bookmarking Important Reports

You can bookmark reports you want to reference frequently:
1. Open the report you want to bookmark
2. Click the bookmark icon
3. The report stays pinned at the top of your history

Bookmarks help you quickly access:
- Baseline reports from major policy reviews
- Reports showing significant issues
- Before/after comparisons

## Report Details

Each saved report includes:
- **Generation date** - When it was created
- **Documents analyzed** - Which policies were included
- **Finding count** - Total findings and breakdown by type
- **Full details** - All findings identified at that time

## How Long Reports Are Kept

Reports remain in your history indefinitely. They're stored as snapshots of your policy system at specific points in time.

## Comparing Reports

To compare two reports:
1. Open an older report and note the conflicts and count
2. Open a newer report
3. Compare the findings manually

Look for:
- Conflicts that were resolved
- New conflicts that appeared
- Overall trend (increasing or decreasing)

## Downloading Report History

You can download individual reports as PDF files to share with colleagues or keep in your records. See [Downloading Reports](/help/reports/downloading-reports) for details.

## Managing Your History

**Removing reports**
Currently, you cannot delete individual reports from the history. All generated reports remain available.

**Storage limits**
There's no limit to how many reports you can generate and save.

---

**Related Articles:**
- [Generating Reports](/help/reports/generating-reports)
- [Reading Report Results](/help/reports/reading-report-results)
- [Downloading Reports](/help/reports/downloading-reports)
`,

  'downloading-reports': `

Save your analysis reports as PDF files to share with colleagues or keep in your records.

## Why Download Reports

**Share with your team**
Email the report to colleagues who don't have Policy Central access.

**Keep records**
Save copies for compliance documentation or audit trails.

**Print for meetings**
Create hard copies to review during team discussions.

**Archive milestones**
Save reports from key moments, like before major policy overhauls.

## How to Download

1. Open the report you want to download
2. Click the **Download** or **Export** button
3. Choose a location on your computer
4. Save the file

The report downloads as a PDF file named with the date and time it was generated.

## What's Included

The downloaded PDF contains:
- Generation date and time
- Total conflict count and breakdown by type
- All conflict details with descriptions
- Document references and section numbers
- Content excerpts showing the conflicts

The PDF matches what you see on screen, formatted for easy reading and printing.

## File Format

Reports download as PDF files because:
- **Universal compatibility** - Opens on any device
- **Professional appearance** - Clean formatting for sharing
- **Print-ready** - Looks good on paper
- **Preserves layout** - Graphics and formatting stay intact

## File Size

Report PDFs typically range from 1-10 MB depending on how many conflicts are found and how much context is included. These sizes are easy to email and store.

## Opening Downloaded Reports

Double-click the downloaded file to open it in your default PDF reader:
- Adobe Acrobat Reader
- Preview (Mac)
- Edge/Chrome (built-in viewers)
- Any other PDF software

## Organizing Downloaded Reports

Create a folder structure on your computer for easy organization.

**Example folder structure:**
- Policy Analysis Reports/
  - 2026/
    - January/
    - February/
    - March/

**Name files descriptively:**
- "Policy Analysis - Feb 7 2026.pdf"
- "Baseline Report - 2026-02-07.pdf"

## Sharing Reports

**Email considerations:**
Most email systems accept files up to 10-25 MB, so reports should attach without issues.

**Confidentiality:**
Reports contain details about your policies. Make sure you're sending them securely to authorized recipients only.

**Version control:**
When emailing, include the date in the filename or message so recipients know which version they have.

## Cannot Download?

If the download doesn't work:
1. **Check browser settings** - Make sure downloads aren't blocked
2. **Try a different browser** - Some browsers handle downloads differently
3. **Check disk space** - Make sure you have room for the file
4. **Refresh and retry** - Sometimes a simple refresh fixes the issue

---

**Related Articles:**
- [Report History](/help/reports/report-history)
- [Reading Report Results](/help/reports/reading-report-results)
`,

  'architecture-overview': `

This article explains how Policy Central is built and how the different parts work together. This is for users curious about the technical side.

## System Components

Policy Central consists of several connected systems:

**Web Application:** The interface you see in your browser that displays your library, handles uploads, and communicates with backend services.

**Storage System:** Secure cloud storage that holds your uploaded PDFs and maintains backups.

**Processing Service:** Extracts text from PDFs, breaks content into analyzable sections, and identifies key policy statements.

**Document Library:** Stores searchable document content and enables fast comparison between policies.

**Analysis Engine:** Compares statements across documents, identifies contradictions and overlaps, classifies findings by type, and generates reports.

## Document Processing

Each document goes through these stages:

**1. Uploading**
Your file is securely uploaded to the storage system and a processing job is created.

**2. Processing**
We extract text from your PDF and prepare it for analysis. Large documents are automatically split into smaller pieces for efficient processing.

**3. Processed**
Your document is ready to include in reports.

## Security and Privacy

All documents are encrypted. Only your organization can access your documents. Policy Central runs on enterprise-grade cloud infrastructure.

---

**Related Articles:**
- [Processing Pipeline](/help/technical/processing-pipeline)
- [AI Analysis](/help/technical/ai-analysis)
`,

  'vector-database': `

Policy Central uses vector search technology that understands the meaning of your policies, not just matching keywords.

## How It Helps You

**Finds conflicts based on meaning:**
If one policy says "within 24 hours" and another says "one day," the system recognizes these mean the same thing.

**Works with different wording:**
Catches conflicts even when policies use different terminology like "must" vs "required" or "deadline" vs "due date."

**More thorough analysis:**
Traditional keyword search would miss conflicts where different words express the same concept.

## Example

- Policy A: "Reports must be submitted within 24 hours"
- Policy B: "Officers have one day to file their reports"

Vector search recognizes these are essentially the same requirement, even though the exact words differ.

## Why Results Are Suggestions

The system identifies potential conflicts based on meaning similarity, but you still need to review them. Sometimes similar-sounding statements are intentionally different, which is why human judgment matters.

---

**Related Articles:**
- [AI Analysis](/help/technical/ai-analysis)
- [How Analysis Works](/help/analysis/how-analysis-works)
`,

  'ai-analysis': `

Policy Central uses AI to understand the meaning of your policies and identify contradictions and redundancies across documents.

## What the AI Looks For

**Contradictions:**
Statements that conflict with each other, like different deadlines for the same process or opposite requirements.

**Redundancies:**
The same rule stated in multiple places, which can cause confusion if one gets updated and others don't.

**Classification:**
Each finding is assigned a type — Conflict, Redundancy, or Similarity — based on the nature of the relationship between the policies.

## How to Interpret Results

**Not every flagged item is a problem:**
The AI identifies potential conflicts based on meaning similarity. Some apparent conflicts may be intentional differences for specific situations.

**Context matters:**
Review the full sections, not just the excerpts shown. Understanding the complete context helps you decide if it's a real issue.

**You make the final call:**
The AI suggests potential conflicts; you determine what actually needs fixing based on your organization's needs.

## Why Use AI for This

**Speed:** Analyzes hundreds of pages in minutes instead of days of manual review.

**Thoroughness:** Catches conflicts humans might miss due to volume or fatigue.

**Consistency:** Applies the same standards to every document without bias.

**Scale:** Makes comprehensive policy analysis practical, even with many documents.

## Privacy

Your documents are used only for your analysis, are not shared with other organizations, and are not used to train AI models.

---

**Related Articles:**
- [How Analysis Works](/help/analysis/how-analysis-works)
- [Understanding Conflicts](/help/reports/understanding-conflicts)
`,

  'login-and-auth': `

Learn how to access your Policy Central account securely.

## Logging In

### First Time Access

When you first receive your Policy Central account:

1. Check your email for the invitation
2. Click the link in the email
3. Create your password
4. Log in with your credentials

### Regular Login

**To log in:**
1. Go to the Policy Central website
2. Enter your email address
3. Enter your password
4. Click **Sign In**

## Your Credentials

**Email address:**
Your work email address is your username. This is set by your organization administrator.

**Password:**
You create your own password when you first set up your account.

## Password Requirements

For security, your password must:
- Be at least 8 characters long
- Include at least one uppercase letter
- Include at least one lowercase letter
- Include at least one number
- Include at least one special character (!@#$%^&*)

## Staying Logged In

**Session duration:**
You remain logged in for 8 hours of activity. After that, you'll need to log in again.

**Multiple tabs:**
You can have Policy Central open in multiple browser tabs while logged in.

**Multiple devices:**
You can log in from different devices (work computer, home laptop, tablet).

## Automatic Logout

You'll be logged out automatically if:
- You don't interact with the app for 2 hours
- Your session expires (after 8 hours)
- You click "Sign Out"
- You close all browser windows (depending on browser settings)

## Login Problems

### Forgot Password

See [Password Reset](/help/support/password-reset) for instructions.

### Account Locked

After several failed login attempts, your account may be temporarily locked for security. Wait 15 minutes and try again, or contact your administrator.

### Email Not Recognized

If the system says your email isn't found:
- Check for typos in your email address
- Verify you're using your work email
- Contact your administrator to confirm your account is set up

### Wrong Password

If your password isn't working:
- Check for Caps Lock being on
- Make sure you're typing it correctly
- Use the "Forgot Password" link if needed

## Security Features

**Encryption:**
All login communication is encrypted (HTTPS).

**Password protection:**
Passwords are stored securely and never displayed.

**Session security:**
Your session is protected against hijacking.

**Automatic logout:**
Inactivity logout prevents unauthorized access.

## Multi-Organization Access

If you belong to multiple organizations:
- You may have different accounts for each
- Each account has its own email and password
- Log out and back in to switch between organizations

## Browser Compatibility

Policy Central works with:
- Google Chrome (recommended)
- Mozilla Firefox
- Safari
- Microsoft Edge

Use the latest version of your browser for best security and performance.

## Account Management

**Changing your password:**
Use the "Forgot Password" feature or contact your administrator.

**Updating your email:**
Contact your organization administrator.

**Account issues:**
Contact your administrator or Policy Central support.

## Security Best Practices

**Protect your password:**
- Don't share it with anyone
- Don't write it down where others can see it
- Don't use the same password as other accounts
- Change it if you think it's been compromised

**Log out when done:**
Especially on shared computers, always sign out when finished.

**Be aware of phishing:**
Policy Central will never email you asking for your password.

**Report suspicious activity:**
If you notice unauthorized access, change your password immediately and report it.

---

**Related Articles:**
- [Password Reset](/help/support/password-reset)
- [Reporting Bugs](/help/support/reporting-bugs)
`,

  'password-reset': `

If you forgot your password or need to change it, here's how to reset it.

## Resetting Your Password

### Step 1: Go to the Login Page

Navigate to the Policy Central login page.

### Step 2: Click "Forgot Password"

Look for the "Forgot Password" link below the login form and click it.

### Step 3: Enter Your Email

Type the email address you use to log in to Policy Central.

### Step 4: Check Your Email

You'll receive a password reset email within a few minutes. Check:
- Your inbox
- Your spam/junk folder (sometimes it goes there)

### Step 5: Click the Reset Link

Open the email and click the password reset link. This link is valid for 1 hour.

### Step 6: Create a New Password

Enter your new password twice to confirm it. Make sure it meets the requirements:
- At least 8 characters long
- At least one uppercase letter
- At least one lowercase letter
- At least one number
- At least one special character (!@#$%^&*)

### Step 7: Log In

Once your password is changed, log in with your email and new password.

## Didn't Receive the Email?

**Check spam/junk:**
Reset emails sometimes get filtered. Check all your email folders.

**Wait a few minutes:**
Email delivery can take up to 5 minutes.

**Check the email address:**
Make sure you entered the correct email address you use for Policy Central.

**Try again:**
Request another reset email. You can request multiple times.

**Contact support:**
If you still don't receive it after 10 minutes, contact support.

## Reset Link Expired

Password reset links expire after 1 hour for security.

**What to do:**
- Go back to the login page
- Click "Forgot Password" again
- Request a new reset email
- Complete the process within 1 hour

## Changing Your Password

If you remember your current password but want to change it:
- Currently, use the "Forgot Password" feature
- Or contact your organization administrator

A "Change Password" feature within the app is planned for future updates.

## Security Tips

**Create a strong password:**
Use a mix of different character types and make it memorable but hard to guess.

**Don't reuse passwords:**
Use a unique password for Policy Central, different from other accounts.

**Use a password manager:**
Consider using a password manager to generate and store strong passwords securely.

**Change passwords regularly:**
Update your password every 6-12 months for best security.

## After Resetting

**Log in immediately:**
Test your new password right away to make sure it works.

**Update saved passwords:**
If your browser saved your old password, update it with the new one.

**Make a note:**
Store your new password securely (in a password manager, not written down where others can see).

## Still Having Problems?

If you can't reset your password after following these steps:

**Account locked:**
Too many failed attempts may temporarily lock your account. Wait 15 minutes.

**Account doesn't exist:**
Verify your email address is correct and that your account is set up.

**Technical issues:**
Contact your organization administrator or Policy Central support.

---

**Related Articles:**
- [Login and Authentication](/help/support/login-and-auth)
`,

  'reporting-bugs': `

Found a problem or have feedback? Here's how to report it so we can help.

## What to Report

Please report:
- **Bugs** - Features not working as expected
- **Errors** - Error messages or failed operations  
- **Performance issues** - Slow loading or unresponsive pages
- **Display problems** - Broken layouts or missing elements
- **Usability concerns** - Confusing features or workflows

## How to Report a Bug

### Using the Report a Bug Feature

1. Click your profile icon in the top right
2. Select **Report a Bug** from the menu
3. Fill out the form with:
   - Brief description of the problem
   - What you were trying to do
   - What happened instead
   - Any error messages you saw
4. Click **Submit**

Your report is sent directly to the support team.

### By Email

You can also email: support@policinglab.org

Include the same information as you would in the form.

## What Information to Include

Help us help you faster by providing:

**What you were doing:**
Describe the steps you took before the problem occurred.
Example: "I was uploading a PDF to the library"

**What went wrong:**
Explain what happened that shouldn't have.
Example: "The upload completed but the document shows 'Failed' status"

**Error messages:**
Copy the exact text of any error messages.
Example: "Error: Document processing failed - unable to extract text"

**Your environment:**
- Browser (Chrome, Firefox, Safari, Edge)
- Operating system (Windows, Mac, Linux)
- When it happened (date and time)

**Frequency:**
- Does it happen every time?
- Only sometimes?
- First time it happened?

## Priority Levels

Critical issues (can't log in, can't upload, data loss) get immediate attention. Other issues are prioritized by impact on your work.

## What Happens After You Report

**Acknowledgment:**
You'll receive a confirmation email that we received your report.

**Investigation:**
Our team will review the issue and may contact you for more information.

**Updates:**
For critical issues, we'll keep you informed of progress.

**Resolution:**
Once fixed, you'll be notified. Some fixes deploy automatically; others may require a system update.

## Response Times

**Critical issues:** Within 2 hours during business hours
**High priority:** Within 1 business day
**Medium priority:** Within 3 business days
**Low priority:** Reviewed during regular updates

## Before Reporting

Try these quick fixes first:

**Refresh the page:**
Many temporary issues resolve with a simple refresh.

**Clear your browser cache:**
Old cached files sometimes cause display problems.

**Try a different browser:**
This helps determine if it's browser-specific.

**Check your internet:**
Connection issues can cause upload or loading problems.

**Log out and back in:**
Resets your session and often fixes authentication issues.

If the problem persists after trying these, go ahead and report it.

## Feature Requests

Have an idea for improving Policy Central?

**Use the same reporting process:**
Send feature requests through "Report a Bug" or email support. Mark them as suggestions rather than bugs.

**What to include:**
- What feature you'd like to see
- How it would help your work
- Any examples from other tools

We review all suggestions and consider them for future updates.

## Follow-Up

If we need more information:
- We'll email you with questions
- Please respond to help us resolve the issue faster
- Include the original ticket number in your reply

## Privacy

When you report a bug:
- Your information is used only to resolve the issue
- Document names may be included to help diagnose problems
- Actual document content is not included in reports
- Information is kept confidential

## Common Issues Already Known

Check the Help Center for:
- [Upload Issues](/help/troubleshooting/upload-issues)
- [Can't Generate Report](/help/troubleshooting/cant-generate-report)
- [Processing Stuck](/help/troubleshooting/processing-stuck)

These articles may solve your problem without needing to file a report.

---

**Related Articles:**
- [Active Operations Explained](/help/troubleshooting/active-operations)
`
};
